import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import { Row, TypographyStyles } from "@/libraries/wpl/foundations/src";

import { ArrowPencilPointLeft, ArrowPencilPointRight } from "./illustrations";

export const CareersSection = () => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    const intl = useIntl();

    return (
        <UFOSegment name="homepage-v2-careers">
            <TypographyStyles>
                <section
                    css={careersSectionStyles}
                    ref={scrollRef}
                    className={sectionIsVisible ? "is-visible" : ""}
                >
                    <div className="arrows arrows-left">
                        <ArrowPencilPointRight
                            className="arrow1"
                            rotate={10}
                            color={"1868DB"}
                        />
                        <ArrowPencilPointRight
                            className="arrow2"
                            rotate={8}
                            color={"BC5DF2"}
                        />
                        <ArrowPencilPointRight
                            className="arrow3"
                            rotate={-6}
                            color={"000000"}
                        />
                        <ArrowPencilPointRight
                            className="arrow4"
                            rotate={-13}
                            color={"000000"}
                        />
                        <ArrowPencilPointRight
                            className="arrow5"
                            rotate={-15}
                            color={"FFA900"}
                        />
                        <ArrowPencilPointRight
                            className="arrow6"
                            rotate={-15}
                            color={"1868DB"}
                        />
                    </div>
                    <div className="arrows arrows-right">
                        <ArrowPencilPointLeft
                            className="arrow7"
                            rotate={-10}
                            color={"000000"}
                        />
                        <ArrowPencilPointLeft
                            className="arrow8"
                            rotate={-20}
                            color={"1868DB"}
                        />
                        <ArrowPencilPointLeft
                            className="arrow9"
                            rotate={5}
                            color={"82B536"}
                        />
                        <ArrowPencilPointLeft
                            className="arrow10"
                            rotate={5}
                            color={"1868DB"}
                        />
                        <ArrowPencilPointLeft
                            className="arrow11"
                            rotate={10}
                            color={"FFA900"}
                        />
                        <ArrowPencilPointLeft
                            className="arrow12"
                            rotate={10}
                            color={"000000"}
                        />
                    </div>
                    <Row>
                        <div className="row-width">
                            <h2 className="h1 reduced-font-weight">
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.careersHeader",
                                    defaultMessage: "Help us shape"
                                })}{" "}
                                <span className="h1">
                                    {intl.formatMessage({
                                        id: "HomePageTemplateV2.careersHeaderBold",
                                        defaultMessage: "the future of teamwork"
                                    })}
                                </span>
                            </h2>
                            <Row layout="8">
                                <p className=".body-lg">
                                    {intl.formatMessage({
                                        id: "HomePageTemplateV2.careersParagraph",
                                        defaultMessage:
                                            "At Atlassian, we believe the impossible is possible - together. Join us today to help us inspire teamwork anywhere and everywhere, worldwide."
                                    })}
                                </p>
                            </Row>
                            <Adg4Button variant="secondary" color="black">
                                {intl.formatMessage({
                                    id: "HomePageTemplateV2.careersButton",
                                    defaultMessage: "Explore careers"
                                })}
                            </Adg4Button>
                        </div>
                    </Row>
                </section>
            </TypographyStyles>
        </UFOSegment>
    );
};

const careersSectionStyles = css({
    "@keyframes arrow1": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "20%"
        },
        to: {
            insetInlineStart: "-4%",
            insetBlockStart: "25%"
        }
    },
    "@keyframes arrow2": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "25%"
        },
        to: {
            insetInlineStart: "50%",
            insetBlockStart: "35%"
        }
    },
    "@keyframes arrow3": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "40%"
        },
        to: {
            insetInlineStart: "-2%",
            insetBlockStart: "40%"
        }
    },
    "@keyframes arrow4": {
        from: {
            insetInlineEnd: "110%",
            insetBlockStart: "50%"
        },
        to: {
            insetInlineEnd: "0%",
            insetBlockStart: "50%"
        }
    },
    "@keyframes arrow5": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "62%"
        },
        to: {
            insetInlineStart: "-2%",
            insetBlockStart: "60%"
        }
    },
    "@keyframes arrow6": {
        from: {
            insetInlineStart: "-100%",
            insetBlockStart: "59%"
        },
        to: {
            insetInlineStart: "35%",
            insetBlockStart: "55%"
        }
    },
    "@keyframes arrow7": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "10%"
        },
        to: {
            insetInlineEnd: "-3%",
            insetBlockStart: "10%"
        }
    },
    "@keyframes arrow8": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "10%"
        },
        to: {
            insetInlineEnd: "40%",
            insetBlockStart: "18%"
        }
    },
    "@keyframes arrow9": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "35%"
        },
        to: {
            insetInlineEnd: "67%",
            insetBlockStart: "35%"
        }
    },
    "@keyframes arrow10": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "35%"
        },
        to: {
            insetInlineEnd: "25%",
            insetBlockStart: "35%"
        }
    },
    "@keyframes arrow11": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "43%"
        },
        to: {
            insetInlineEnd: "-5%",
            insetBlockStart: "43%"
        }
    },
    "@keyframes arrow12": {
        from: {
            insetInlineEnd: "-100%",
            insetBlockStart: "73%"
        },
        to: {
            insetInlineEnd: "25%",
            insetBlockStart: "63%"
        }
    },
    "position": "relative",
    "overflow": "hidden",
    "textAlign": "center",
    "padding": "100px 0",
    "display": "flex",
    "flexWrap": "wrap",

    "&.is-visible": {
        svg: {
            "&.arrow1": {
                animationName: "arrow1"
            },
            "&.arrow2": {
                animationName: "arrow2"
            },
            "&.arrow3": {
                animationName: "arrow3"
            },
            "&.arrow4": {
                animationName: "arrow4"
            },
            "&.arrow5": {
                animationName: "arrow5"
            },
            "&.arrow6": {
                animationName: "arrow6"
            },

            "&.arrow7": {
                animationName: "arrow7"
            },
            "&.arrow8": {
                animationName: "arrow8"
            },
            "&.arrow9": {
                animationName: "arrow9"
            },
            "&.arrow10": {
                animationName: "arrow10"
            },
            "&.arrow11": {
                animationName: "arrow11"
            },
            "&.arrow12": {
                animationName: "arrow12"
            }
        }
    },

    ".arrows": {
        "width": "22%",
        "position": "absolute",
        "insetBlockStart": 0,
        "insetBlockEnd": 0,
        "@media (max-width: 768px)": {
            "position": "relative",
            "width": "50%",
            "minHeight": "330px",
            "margin": "0 0 34px",
            "&.arrows-left": {
                transform: "rotate(45deg)"
            },
            "&.arrows-right": {
                transform: "rotate(-45deg)"
            }
        },

        "&.arrows-left": {
            insetInlineStart: 0
        },
        "&.arrows-right": {
            insetInlineEnd: 0
        },

        "svg": {
            position: "absolute",
            animationDuration: "1.5s",
            animationIterationCount: 1,
            animationFillMode: "forwards"
        }
    },

    ".row-width": {
        "padding": `${token("space.0")} ${token("space.500")}`,

        ".half": {
            flexBasis: "100%"
        }
    },
    "h2": {
        marginBlockEnd: token("space.200")
    },
    "p": {
        marginBlockEnd: token("space.400")
    },
    ".reduced-font-weight": {
        fontWeight: 400
    }
});
